import queryString from 'query-string'

import { apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'

import * as redux from './outboundContactLists.redux'

export const fetchContactLists = () => {
  return async (dispatch) => {
    dispatch(redux.setLoading({ contactLists: true }))

    try {
      const response = await fetchingAPI(`${apiService.core}/api/contact_lists`, 'GET', dispatch)
      dispatch(redux.setContactLists(response))
    } catch (error) {
      toast.error('Failed to fetch contact lists', { cause: error })
    } finally {
      dispatch(redux.setLoading({ contactLists: false }))
    }
  }
}

export const fetchContacts = (contactListId, { limit = 100, offset = 0 } = {}) => {
  return async (dispatch) => {
    dispatch(redux.setLoading({ contacts: true }))

    try {
      const params = queryString.stringify({ contact_list_id: contactListId, limit, offset })
      const response = await fetchingAPI(
        `${apiService.core}/api/contacts?${params}`,
        'GET',
        dispatch
      )
      dispatch(redux.setContacts(response))
    } catch (error) {
      toast.error('Failed to fetch contacts', { cause: error })
    } finally {
      dispatch(redux.setLoading({ contacts: false }))
    }
  }
}

export const exportContacts = (contactListId, contactListName) => {
  return async (dispatch) => {
    dispatch(redux.setLoading({ csvExport: true }))

    try {
      const params = queryString.stringify({ contact_list_id: contactListId })
      // The endpoint streams a CSV; handleResponse resolves the response body
      // (a ReadableStream) for text/csv, which we collect into a Blob to download.
      const stream = await fetchingAPI(
        `${apiService.core}/api/contacts/export?${params}`,
        'GET',
        dispatch
      )
      const blob = await new Response(stream).blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${contactListName} Contacts.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      toast.error('Failed to export contacts', { cause: error })
    } finally {
      dispatch(redux.setLoading({ csvExport: false }))
    }
  }
}

export const createContactList = ({ name, description, dnc_scrub_acknowledged }) => {
  return async (dispatch) => {
    try {
      const response = await fetchingAPI(
        `${apiService.core}/api/contact_lists`,
        'POST',
        dispatch,
        JSON.stringify({ name, description, dnc_scrub_acknowledged })
      )
      toast.success('Contact list created')
      dispatch(fetchContactLists())
      return response
    } catch (error) {
      toast.error('Failed to create contact list', { cause: error })
      return null
    }
  }
}

export const updateContactList = (contactListId, values) => {
  return async (dispatch) => {
    try {
      await fetchingAPI(
        `${apiService.core}/api/contact_lists/${contactListId}`,
        'PUT',
        dispatch,
        JSON.stringify(values)
      )
      toast.success('Contact list updated')
      dispatch(fetchContactLists())
    } catch (error) {
      toast.error('Failed to update contact list', { cause: error })
    }
  }
}

export const deleteContactList = (contactListId) => {
  return async (dispatch) => {
    try {
      await fetchingAPI(
        `${apiService.core}/api/contact_lists/${contactListId}`,
        'PUT',
        dispatch,
        JSON.stringify({ deleted: true })
      )
      toast.success('Contact list deleted')
      dispatch(fetchContactLists())
    } catch (error) {
      toast.error('Failed to delete contact list', { cause: error })
    }
  }
}

export const createContact = (contactListId, values) => {
  return async (dispatch) => {
    try {
      const response = await fetchingAPI(
        `${apiService.core}/api/contact_lists/${contactListId}/contacts`,
        'POST',
        dispatch,
        JSON.stringify(values)
      )
      toast.success('Contact created')
      dispatch(fetchContactLists())
      return response
    } catch (responseError) {
      toast.error(responseError?.detail || 'Failed to create contact')
      return null
    }
  }
}

export const updateContact = (contactId, values) => {
  return async (dispatch) => {
    try {
      const response = await fetchingAPI(
        `${apiService.core}/api/contacts/${contactId}`,
        'PUT',
        dispatch,
        JSON.stringify(values)
      )
      toast.success('Contact updated')
      dispatch(fetchContactLists())
      return response
    } catch (responseError) {
      toast.error(responseError?.detail || 'Failed to update contact')
      return null
    }
  }
}

export const deleteContact = (contactId) => {
  return async (dispatch) => {
    try {
      await fetchingAPI(`${apiService.core}/api/contacts/${contactId}`, 'DELETE', dispatch)
      toast.success('Contact deleted')
      dispatch(fetchContactLists())
      return true
    } catch (responseError) {
      toast.error(responseError?.detail || 'Failed to delete contact')
      return false
    }
  }
}

const submitContacts = (contactListId, file, method) => {
  const params = queryString.stringify({ contact_list_id: contactListId })
  const formData = new FormData()
  formData.append('file', file)

  return async (dispatch) => {
    try {
      await fetchingAPI(
        `${apiService.core}/api/contacts?${params}`,
        method,
        dispatch,
        formData,
        undefined,
        undefined,
        undefined,
        undefined,
        false
      )
      toast.success('Contacts uploaded successfully')
      dispatch(fetchContactLists())
      return { success: true }
    } catch (responseError) {
      const message =
        responseError?.error ||
        responseError?.detail ||
        responseError?.message ||
        'Failed to upload contacts'
      return { success: false, error: Array.isArray(message) ? message : [message] }
    }
  }
}

export const uploadContacts = (contactListId, file) => {
  return submitContacts(contactListId, file, 'POST')
}

export const syncContacts = (contactListId, file) => {
  return submitContacts(contactListId, file, 'PUT')
}
